import { Injectable, signal, untracked } from '@angular/core';

/**
 * Contadores livianos del estado HTTP del cliente. Los alimenta
 * `operationalTimeoutInterceptor` y los lee la pantalla de cocina (chip de
 * diagnostico gateado por flag) para confirmar en vivo si un congelamiento
 * bajo alto volumen se debe a agotamiento del pool de conexiones del navegador
 * (~6 por dominio): si `inFlight` queda clavado en el tope y no baja, los
 * requests nuevos quedan encolados para siempre = pantalla "congelada".
 */
@Injectable({ providedIn: 'root' })
export class HttpDiagnosticsService {
  /** Requests actualmente en vuelo (sin resolver) a traves del HttpClient. */
  readonly inFlight = signal(0);
  /** Maximo historico de `inFlight` en esta sesion (se acerca al tope = sospechoso). */
  readonly peakInFlight = signal(0);
  /** Cuantas veces un request operativo se corto por timeout (libero su conexion). */
  readonly timeoutCount = signal(0);
  /** Ultima URL que sufrio timeout — pista de cual endpoint se cuelga. */
  readonly lastTimeoutUrl = signal<string | null>(null);

  // Estos contadores los muta el `operationalTimeoutInterceptor` en cada request.
  // El interceptor corre de forma sincrona al hacer `.subscribe()`, y ese subscribe
  // puede ocurrir dentro del contexto reactivo de un `effect()` (p.ej. el chat del
  // pedido carga su historial desde un effect). Sin `untracked`, leer un signal aca
  // (`inFlight()`) lo registraria como dependencia del effect y la escritura posterior
  // (`inFlight.set`) lo re-dispararia en bucle sincrono infinito -> el navegador
  // crashea. Las mutaciones de estos contadores NUNCA deben crear dependencias en
  // quien dispara el request; solo el chip de diagnostico los lee reactivamente.
  increment(): void {
    untracked(() => {
      const next = this.inFlight() + 1;
      this.inFlight.set(next);
      if (next > this.peakInFlight()) {
        this.peakInFlight.set(next);
      }
    });
  }

  decrement(): void {
    untracked(() => this.inFlight.set(Math.max(0, this.inFlight() - 1)));
  }

  recordTimeout(url: string): void {
    untracked(() => {
      this.timeoutCount.update(v => v + 1);
      this.lastTimeoutUrl.set(url);
    });
  }
}
